import { useEffect, useRef, useState } from 'react'
import CanvasJSReact from '@canvasjs/react-charts'

import { CHART_DEFAULT_OPTIONS, SUBTITLE_FONT_SIZE, TITLE_FONT_SIZE, UserUnitSettings } from '../../common/constants'
import { CollapsibleSection } from '../CollapsibleSection'
import { formatNumber } from '../../common/utils'
import { DeviceInfo, SensorProduct, UnitCategoryId } from '../../common/enums'
import { convertBaseToDisplay } from '../../common/conversions'
import { BadgerSDIChartSubHeader } from '../BadgerSDIChartSubHeader'
import { UNIT_CONVERSIONS } from '../../common/unitConversions'
import { Viewport } from '../../common/types'
import { createAxisLabelFormatter, alertMarkersToStripLines, AlertMarker, getHiddenRangeStripLines } from '../../utils/chartHelpers'

const { CanvasJSChart } = CanvasJSReact

const UNIT_ID_ACRE_FOOT = 3 // Acre-Foot
// const DEFAULT_VOLUME_RATE_UNITS = 6 // Gallon per minute

// Last-resort reading interval (minutes) when the device config carries no
// schedule frequency and the readings are too sparse to infer one.
const FALLBACK_SCHEDULE_FREQUENCY_MINUTES = 15

// Rate is always held in base units (m3/s) before display conversion, matching
// what the API stores for a Volume Rate reading.
const VOLUME_RATE_BASE_CONVERSION = {
  ...UNIT_CONVERSIONS[UnitCategoryId.VolumeRate],
  current: { 65: 'cubic meters / second', formula: '@' },
}

export interface FlowMeterSettings {
  volume: {
    unitId: number
    precision: number
  }
  volumeRate: {
    unitId: number
    precision: number
  }
  totalVolume: {
    unitId: number
    precision: number
  }
}

interface DataPoints {
  volume: { x: number; y?: number }[]
  totals: { x: number; y?: number }[]
  rate: { x: number; y?: number }[]
}

/**
 * A Badger SDI port's readings normalised into the two base-unit series the
 * chart draws from, regardless of which unit category the API actually
 * returned. `rateBase` is only populated when the rate is a stored reading.
 */
export interface VolumeSource {
  volumeBase: Record<string, number> // m3 per reading, keyed by unix seconds
  rateBase?: Record<string, number> // m3/s, keyed by unix seconds
  hidden: Record<string, boolean>
  volumeFormulas: Record<string, string>
  volumeAbbreviations: Record<string, string>
}

function findByCategory(sensorReadings: any, longName: string): any {
  if (!Array.isArray(sensorReadings)) return undefined
  return sensorReadings.find((reading: any) => reading?.metadata?.units?.category?.longName === longName)
}

/**
 * Median gap between consecutive readings, in seconds. Used only when the
 * device config has no schedule frequency — the median ignores the outliers a
 * missed message or a config change introduces.
 */
function inferIntervalSeconds(readings: Record<string, number>): number | undefined {
  const timestamps = Object.keys(readings)
    .map(Number)
    .filter((ts) => Number.isFinite(ts))
    .sort((a, b) => a - b)

  if (timestamps.length < 2) return undefined

  const gaps: number[] = []
  for (let i = 1; i < timestamps.length; i++) {
    const gap = timestamps[i] - timestamps[i - 1]
    if (gap > 0) gaps.push(gap)
  }

  if (gaps.length === 0) return undefined

  gaps.sort((a, b) => a - b)
  return gaps[Math.floor(gaps.length / 2)]
}

/**
 * Resolve which reading category this port's chart is built from.
 *
 * The SDI decoder emits ONLY a Volume Rate reading: it divides the pulse volume
 * by the device's schedule frequency and never stores the volume itself (see
 * sigfox-decoder/converters/badgermeterSDI.ts, which returns a single
 * UnitCategory.VolumeRate entry). Legacy SDI data does carry a Volume category,
 * so prefer that when present and otherwise invert the decoder's own
 * arithmetic: volume = rate x scheduleFrequency x 60.
 *
 * The schedule frequency is used deliberately in place of the gap between
 * consecutive timestamps — the stored rate was computed against the schedule,
 * so a missed message would otherwise be charged a full extra interval of
 * volume it never measured.
 *
 * Returns undefined when the port has neither category, which is what keeps the
 * chart from assuming a Volume series that does not exist.
 */
export function resolveVolumeSource(sensorReadings: any, scheduleFrequencyMinutes: number): VolumeSource | undefined {
  const volumeData = findByCategory(sensorReadings, 'Volume')
  const rateData = findByCategory(sensorReadings, 'Volume Rate')

  if (volumeData) {
    return {
      volumeBase: volumeData.readings || {},
      // Take the stored rate whenever the port reports one, even though the
      // volume came from elsewhere: /readings-fast reconstructs the SDI volume
      // series and returns it alongside the rate it was derived from, so both
      // are present. Inferring the rate from the gap between readings instead
      // would charge a missed message's silence to the following reading.
      rateBase: rateData?.readings,
      hidden: volumeData.hidden || {},
      volumeFormulas: volumeData.metadata.units.conversions.formulas,
      volumeAbbreviations: volumeData.metadata.units.conversions.abbreviations,
    }
  }

  if (!rateData) return undefined

  const rateBase: Record<string, number> = rateData.readings || {}

  const intervalSeconds =
    scheduleFrequencyMinutes > 0
      ? scheduleFrequencyMinutes * 60
      : inferIntervalSeconds(rateBase) ?? FALLBACK_SCHEDULE_FREQUENCY_MINUTES * 60

  const volumeBase: Record<string, number> = {}

  Object.keys(rateBase).forEach((ts) => {
    volumeBase[ts] = Number(rateBase[ts]) * intervalSeconds
  })

  return {
    volumeBase,
    rateBase,
    hidden: rateData.hidden || {},
    // The rate reading's own metadata describes m3/s, so the Volume category's
    // conversion tables have to come from the static definitions instead.
    volumeFormulas: UNIT_CONVERSIONS[UnitCategoryId.Volume].formulas,
    volumeAbbreviations: UNIT_CONVERSIONS[UnitCategoryId.Volume].abbreviations,
  }
}

interface Series {
  dataPoints: DataPoints
  totalVolume: number
  totalVolumeInAcreFoot: number
  maxVolume: number
  maxRate: number
  maxTotals: number
}

const EMPTY_SERIES: Series = {
  dataPoints: { volume: [], totals: [], rate: [] },
  totalVolume: 0,
  totalVolumeInAcreFoot: 0,
  maxVolume: 0,
  maxRate: 0,
  maxTotals: 0,
}

/**
 * Build the volume/total/rate series in display units from a resolved source.
 * `includeHidden` drives the ghost overlay, which plots the same maths over the
 * timestamps the filtered series leaves out.
 */
export function buildSeries(
  source: VolumeSource | undefined,
  viewport: Viewport,
  settings: FlowMeterSettings,
  includeHidden: boolean
): Series {
  if (!source) return { ...EMPTY_SERIES, dataPoints: { volume: [], totals: [], rate: [] } }

  const dataPoints: DataPoints = { volume: [], totals: [], rate: [] }
  let totalVolume = 0
  let totalVolumeInAcreFoot = 0
  let maxVolume = 0
  let maxRate = 0
  let maxTotals = 0
  let previousTimestamp = -1

  Object.keys(source.volumeBase)
    .filter((ts) => includeHidden || !source.hidden[ts])
    .forEach((ts) => {
      const timestamp = Number(ts) * 1000
      if (timestamp < viewport.minimum || timestamp > viewport.maximum) return

      const volumeInBase = Number(source.volumeBase[ts])

      // A stored rate is used as-is; only timestamps with no rate of their own
      // have to infer one from the gap since the previous reading. Checked per
      // timestamp rather than per series: the two categories are stored as
      // separate rows and a window can hold one without the other.
      const storedRate = source.rateBase?.[ts]
      const rateInBase =
        storedRate != null
          ? Number(storedRate)
          : previousTimestamp !== -1
          ? volumeInBase / ((timestamp - previousTimestamp) / 1000)
          : 0

      const volume = convertBaseToDisplay(volumeInBase, source.volumeFormulas[settings.volume.unitId])
      const rate = convertBaseToDisplay(rateInBase, VOLUME_RATE_BASE_CONVERSION.formulas[settings.volumeRate.unitId])

      totalVolume += volume
      totalVolumeInAcreFoot += convertBaseToDisplay(volumeInBase, source.volumeFormulas[UNIT_ID_ACRE_FOOT])

      if (totalVolume > maxTotals) maxTotals = totalVolume
      if (volume > maxVolume) maxVolume = volume
      if (rate > maxRate) maxRate = rate

      dataPoints.volume.push({ x: timestamp, y: volume })
      dataPoints.totals.push({ x: timestamp, y: totalVolume })
      dataPoints.rate.push({ x: timestamp, y: rate })

      previousTimestamp = timestamp
    })

  return { dataPoints, totalVolume, totalVolumeInAcreFoot, maxVolume, maxRate, maxTotals }
}

interface Props {
  device: DeviceInfo
  sigfoxId: string
  isLoading: boolean
  sensorPort: string
  sensorProduct: SensorProduct
  sensorReadings?: any

  viewport: Viewport

  currentTime: number
  userUnitSettings: UserUnitSettings

  zoomEnabled?: boolean
  selectXRange?: number[]
  addTimestamp?: (timestamp: number) => void
  showHiddenData?: boolean
  alertMarkers?: AlertMarker[]
}

export function BadgerSDIChart({
  device,
  sigfoxId,
  isLoading,
  sensorPort,
  sensorReadings,
  sensorProduct,
  viewport,
  userUnitSettings,
  zoomEnabled = true,
  selectXRange = [],
  addTimestamp = (timestamp: number) => {},
  showHiddenData = false,
  alertMarkers,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const xRef = useRef(0)

  const [showVolume, setShowVolume] = useState(true)
  const [showRate, setShowRate] = useState(true)
  const [showTotals, setShowTotals] = useState(true)

  // Register click listener ONCE when chart mounts (not on every hover)
  // Uses retry mechanism since CanvasJS renders the canvas asynchronously
  // NOTE: CanvasJS creates multiple canvas elements - we need the LAST one (topmost layer)
  useEffect(() => {
    if (!containerRef.current) return

    let canvas: HTMLCanvasElement | null = null
    let retryCount = 0
    const maxRetries = 20
    let timeoutId: ReturnType<typeof setTimeout>

    function onClickHandler() {
      addTimestamp(xRef.current)
    }

    function tryAttachListener() {
      const canvases = containerRef.current?.querySelectorAll('.canvasjs-chart-canvas')
      canvas = canvases && canvases.length > 0 ? (canvases[canvases.length - 1] as HTMLCanvasElement) : null
      if (canvas) {
        canvas.addEventListener('click', onClickHandler)
      } else if (retryCount < maxRetries) {
        retryCount++
        timeoutId = setTimeout(tryAttachListener, 100)
      }
    }

    tryAttachListener()

    return () => {
      clearTimeout(timeoutId)
      if (canvas) {
        canvas.removeEventListener('click', onClickHandler)
      }
    }
  }, [addTimestamp])

  // Default unit settings to prevent crash when userUnitSettings is not fully loaded
  const defaultUnit = { unitId: 1, precision: 2 }
  const settings = {
    volume: userUnitSettings[UnitCategoryId.Volume] ?? defaultUnit,
    volumeRate: userUnitSettings[UnitCategoryId.VolumeRate] ?? defaultUnit,
    totalVolume: userUnitSettings[UnitCategoryId.TotalVolume] ?? defaultUnit,
  }

  /**
   * Whether the account's unit preferences have arrived at all.
   *
   * This used to require Volume, VolumeRate AND TotalVolume to be present, and
   * rendered a spinner forever when any one was missing. That is not a loading
   * state: /user-unit-settings returns only the categories a user has actually
   * chosen for, so an account that never opened the units page was simply
   * absent a key and its flow charts span until the tab was closed — while
   * every non-flow chart on the same page drew normally.
   *
   * The server now fills missing categories with their base unit, so the
   * fallbacks above are a belt on top of braces. What matters here is only that
   * nothing has loaded yet, which is a genuine and temporary condition.
   */
  const settingsLoaded = Object.keys(userUnitSettings).length > 0

  // Check for readings - needs to be before any early return
  const hasReadings = checkForReadings()

  // Early return if settings not loaded to prevent crash in generateDataPoints
  // This must come AFTER all hooks
  if (!settingsLoaded) {
    return (
      <div className={tailwind.container}>
        <div className="animate-pulse bg-gray-200 h-64 rounded" />
      </div>
    )
  }

  // Resolve the reading category this chart draws from. Undefined means the
  // port reported neither Volume nor Volume Rate, in which case every series
  // below stays empty rather than assuming a Volume entry that isn't there.
  const volumeSource =
    hasReadings && sensorReadings ? resolveVolumeSource(sensorReadings, device.scheduledMessagesFrequency) : undefined

  const volumeConversions: any = volumeSource ? volumeSource.volumeAbbreviations : null
  const volumeRateConversions: any = volumeSource ? VOLUME_RATE_BASE_CONVERSION.abbreviations : null

  const titleFontSize = 16
  const labelFontSize = 16
  const { dataPoints, totalVolume, totalVolumeInAcreFoot, maxVolume, maxRate, maxTotals } = generateDataPoints()

  // Ghost data for hidden timestamps (shown when showHiddenData is true)
  const ghostDataPoints: DataPoints = showHiddenData
    ? buildSeries(volumeSource, viewport, settings, true).dataPoints
    : { volume: [], totals: [], rate: [] }

  const hiddenStripLines = showHiddenData ? getHiddenRangeStripLines(volumeSource?.hidden || {}) : []

  function generateDataPoints(): Series {
    const series = buildSeries(volumeSource, viewport, settings, false)
    const { dataPoints } = series

    // pad the series so the chart spans the full viewport even when the
    // readings only cover part of it
    dataPoints.volume.unshift({ x: viewport.minimum, y: undefined })
    dataPoints.totals.unshift({ x: viewport.minimum, y: undefined })
    dataPoints.rate.unshift({ x: viewport.minimum, y: undefined })

    dataPoints.volume.push({ x: viewport.maximum, y: undefined })
    dataPoints.totals.push({ x: viewport.maximum, y: undefined })
    dataPoints.rate.push({ x: viewport.maximum, y: undefined })

    return series
  }

  const flowVolume = {
    name: 'Volume',
    type: 'column',
    axisYType: 'secondary',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'blue',
    visible: showVolume,
    fillOpacity: 0.4,
    dataPoints: showVolume ? dataPoints.volume : [{ x: 0, y: 0 }],
  }

  const flowRate = {
    name: 'Flow Rate',
    axisYType: 'primary',
    type: 'line',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'red',
    visible: showRate,
    fillOpacity: 0.1,
    dataPoints: showRate ? dataPoints.rate : [{ x: 0, y: 0 }],
  }

  const flowTotals = {
    name: 'Flow Total',
    axisYType: 'secondary',
    type: 'area',
    showInLegend: true,
    xValueType: 'dateTime',
    color: 'lightgreen',
    visible: showTotals,
    fillOpacity: 0.4,
    dataPoints: showTotals ? dataPoints.totals : [{ x: 0, y: 0 }],
  }

  const maxVolumeTotal = Math.max(showVolume ? maxVolume : 0, showTotals ? maxTotals : 0) || 10

  const maxVolumeRateTotal = Math.max(showRate ? maxRate : 0) || 10

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    zoomEnabled,
    exportFileName: `${device.deviceName}_FlowMeter`,
    title: {
      text: 'Flow Meter',
      fontSize: TITLE_FONT_SIZE,
    },
    subtitles: [
      {
        fontSize: SUBTITLE_FONT_SIZE,
        text: `${device.deviceName} (Site: ${device.siteName?.trim()})`,
      },
    ],
    willReadFrequently: true,
    dataPointWidth: 4,
    toolTip: {
      shared: true,
      contentFormatter: (e: any) => {
        // const date = new Date(new Date(e.entries[0].dataPoint.x).setSeconds(0))
        const date = new Date(e.entries[0].dataPoint.x)
        var content = `<span style="color:${e.color}">${date.toLocaleString()}</span><br/>`

        for (var i = 0; i < e.entries.length; i++) {
          if (e.entries[i].dataSeries.name.includes('(hidden)')) continue
          const name = e.entries[i].dataSeries.name
          const precision = name === 'Flow Rate' ? settings.volumeRate.precision : settings.volume.precision
          const value = e.entries[i].dataPoint.y
          const color = e.entries[i].dataSeries.color
          const units =
            e.entries[i].dataSeries.name === 'Flow Rate'
              ? volumeRateConversions?.[settings.volumeRate.unitId] ?? ''
              : volumeConversions?.[settings.volume.unitId] ?? ''
          content += `<span style="color:${color}">${name}:</span> <strong> ${
            value !== undefined ? formatNumber(value, precision) : ''
          } ${value !== undefined ? units : ''}</strong><br/>`
        }

        xRef.current = e.entries[0].dataPoint.x

        return content
      },
    },

    // subtitles: [
    //   {
    //     text: isLoading ? null : hasReadings ? null : 'RIC has no data for this time period',
    //     backgroundColor: 'red',
    //     fontColor: 'white',
    //     //Uncomment properties below to see how they behave
    //     margin: 10,
    //     padding: 15,
    //     cornerRadius: 5,
    //     fontSize: 20,
    //     fontWeight: 'bold',
    //     verticalAlign: 'top',
    //     dockInsidePlotArea: true,
    //   },
    // ],

    axisX: [
      {
        labelFormatter: createAxisLabelFormatter(),
        viewportMinimum: viewport?.minimum || null,
        viewportMaximum: viewport?.maximum || null,
        // labelAngle: -90,
        stripLines: [
          ...selectXRange.map((value) => {
            return {
              value,
              color: 'red',
              labelPlacement: 'outside',
            }
          }),

          selectXRange.length === 2 && {
            startValue: selectXRange[0],
            endValue: selectXRange[1],
            color: '#d8d8d8',
          },

          ...hiddenStripLines,
          ...alertMarkersToStripLines(alertMarkers),
        ],
      },
    ],
    axisY: [
      {
        includeZero: false,
        title: `Flow Rate (${volumeRateConversions === null ? '' : volumeRateConversions[settings.volumeRate.unitId]})`,
        suffix: '',
        minimum: 0,
        maximum: maxVolumeRateTotal * 2.5,
        titleFontSize,
        labelFontSize,
        dataPointWidth: 500,
      },
    ],
    axisY2: [
      {
        includeZero: false,
        title: `Volume (${volumeConversions === null ? '' : volumeConversions[settings.volume.unitId]})`,
        suffix: '',
        minimum: 0,
        maximum: maxVolumeTotal * 1.3,
        titleFontSize,
        labelFontSize,
        margin: 10,
        dataPointWidth: 500,
      },
    ],
    data: [
      ...(showHiddenData && ghostDataPoints.volume.length > 0
        ? [
            {
              name: 'Volume (hidden)',
              type: 'column',
              axisYType: 'secondary',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              fillOpacity: 0.15,
              markerSize: 0,
              dataPoints: ghostDataPoints.volume,
              toolTipContent: null,
            },
            {
              name: 'Flow Rate (hidden)',
              axisYType: 'primary',
              type: 'line',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              lineThickness: 2,
              markerSize: 0,
              dataPoints: ghostDataPoints.rate,
              toolTipContent: null,
            },
            {
              name: 'Flow Total (hidden)',
              axisYType: 'secondary',
              type: 'area',
              showInLegend: false,
              xValueType: 'dateTime',
              color: '#aaaaaa',
              fillOpacity: 0.15,
              markerSize: 0,
              dataPoints: ghostDataPoints.totals,
              toolTipContent: null,
            },
          ]
        : []),
      flowRate,
      flowVolume,
      flowTotals,
    ],
    legend: {
      itemclick: (e: any) => {
        switch (e.dataSeries.name) {
          case 'Volume':
            setShowVolume(!showVolume)
            break
          case 'Flow Rate':
            setShowRate(!showRate)
            break
          case 'Flow Total':
            setShowTotals(!showTotals)
            break
        }
      },
    },
  }

  function checkForReadings() {
    return (
      sensorReadings &&
      Object.values(sensorReadings).filter(
        (data: any) => {
          const hiddenMap = data.hidden || {}
          return Object.keys(data.readings || {}).filter((timestamp) => {
            if (hiddenMap[timestamp]) return false  // Skip hidden readings
            const ts = Number(timestamp) * 1000
            return ts >= viewport.minimum && ts <= viewport.maximum
          }).length > 0
        }
      ).length > 0
    )
  }

  function getLastReadingTimestamp(): number {
    if (!volumeSource) return 0
    const timestamps = Object.keys(volumeSource.volumeBase)
    return Number(timestamps[timestamps.length - 1]) || 0
  }

  const currentRate = dataPoints.rate?.filter((data) => data.y !== undefined).slice(-1)[0]?.y

  // const title = `${device.config.device.name}: ${device.config.site.name || '-'}`
  const title = 'Flow Meter'

  return (
    <div className={tailwind.container}>
      <CollapsibleSection
        title={title}
        // mode={mode}
        // setMode={setMode}
        open={true}
        isLoading={isLoading}
      >
        <div className={tailwind.collapsibleSection}>
          <BadgerSDIChartSubHeader
            sigfoxId={sigfoxId}
            sensorPort={sensorPort}
            sensorProduct={sensorProduct}
            volumeAbbreviations={volumeConversions}
            currentRate={currentRate || 0}
            totalVolume={totalVolume || 0}
            totalVolumeInAcreFoot={totalVolumeInAcreFoot || 0}
            lastReading={getLastReadingTimestamp()}
            settings={settings}
          />
        </div>
      </CollapsibleSection>
      <div ref={containerRef}>
        <CanvasJSChart options={options} />
      </div>
    </div>
  )
}

const tailwind = {
  container: 'mb-4',
  collapsibleSection: 'pb-2',
}
